import { useState, useEffect, useRef, useCallback } from 'react';
import { EditorView, basicSetup } from 'codemirror';
import { sql } from '@codemirror/lang-sql';
import { oneDark } from '@codemirror/theme-one-dark';
import { EditorState } from '@codemirror/state';
import { useArtifactStore } from '../hooks/useArtifactStore';
import type { WidgetProps } from './WidgetRegistry';
import type { SqlContent } from '../services/api';

export function SqlEditorWidget({ widgetProps }: WidgetProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);
  const {
    artifacts, activeArtifact, testing, versions,
    loadArtifacts, selectArtifact, runTest, saveVersion, promote,
    setEditorBuffer, pendingEditorLoad, requestEditorLoad, loadVersions,
  } = useArtifactStore();

  const [paramValues, setParamValues] = useState<Record<string, string>>({});
  const [selectedId, setSelectedId] = useState<string>(widgetProps?.artifactId as string || '');
  const [dismissedDraftId, setDismissedDraftId] = useState<string>('');
  const [autoLoadedDraftId, setAutoLoadedDraftId] = useState<string>('');

  // Copilot draft proposal (KB Doc 139): newest version authored by the
  // Copilot, still a draft, newer than what the editor is showing.
  const copilotDraft = versions.find(v =>
    v.isDraft && !v.isLive && v.createdBy === 'studio-copilot' &&
    activeArtifact && v.version > activeArtifact.version && v.id !== dismissedDraftId);

  // Poll versions while an artifact is open so Copilot proposals surface live
  useEffect(() => {
    if (!activeArtifact) return;
    const t = setInterval(() => loadVersions(activeArtifact.artifactId), 15_000);
    return () => clearInterval(t);
  }, [activeArtifact?.artifactId]);

  // Copilot "Load" action — replace the CodeMirror buffer
  useEffect(() => {
    if (pendingEditorLoad !== null && viewRef.current) {
      viewRef.current.dispatch({
        changes: { from: 0, to: viewRef.current.state.doc.length, insert: pendingEditorLoad },
      });
      setEditorBuffer(pendingEditorLoad);
      requestEditorLoad(null);
    }
  }, [pendingEditorLoad]);

  // OD-2: auto-load the Copilot's proposed draft into the editor the moment it
  // appears (once per draft). Promotion stays human-only — the banner offers
  // Revert-to-live / Diff / Dismiss.
  useEffect(() => {
    if (copilotDraft && copilotDraft.id !== autoLoadedDraftId) {
      requestEditorLoad((copilotDraft.content as SqlContent).sqlTemplate);
      setAutoLoadedDraftId(copilotDraft.id);
    }
  }, [copilotDraft?.id]);

  useEffect(() => { loadArtifacts('sql'); }, []);

  useEffect(() => {
    if (selectedId) selectArtifact(selectedId);
  }, [selectedId]);

  useEffect(() => {
    if (!editorRef.current || !activeArtifact || activeArtifact.content.type !== 'sql') return;
    const content = activeArtifact.content as SqlContent;

    if (viewRef.current) viewRef.current.destroy();

    const state = EditorState.create({
      doc: content.sqlTemplate,
      extensions: [
        basicSetup, sql(), oneDark, EditorView.lineWrapping,
        // Mirror the buffer to the store so the Copilot panel sees live edits
        EditorView.updateListener.of(u => {
          if (u.docChanged) setEditorBuffer(u.state.doc.toString());
        }),
      ],
    });

    viewRef.current = new EditorView({ state, parent: editorRef.current });
    setEditorBuffer(content.sqlTemplate);

    const defaults: Record<string, string> = {};
    content.params.forEach(p => { defaults[p.name] = ''; });
    setParamValues(defaults);

    return () => { viewRef.current?.destroy(); viewRef.current = null; };
  }, [activeArtifact?.id]);

  const getCurrentSql = useCallback(() => {
    return viewRef.current?.state.doc.toString() || '';
  }, []);

  const handleTest = () => {
    if (!activeArtifact) return;
    const parsed: Record<string, unknown> = {};
    Object.entries(paramValues).forEach(([k, v]) => {
      const param = (activeArtifact.content as SqlContent).params.find(p => p.name === k);
      if (param?.type === 'number') parsed[k] = Number(v) || 0;
      else parsed[k] = v;
    });
    runTest(activeArtifact.artifactId, parsed, getCurrentSql());
  };

  const handleSave = () => {
    if (!activeArtifact) return;
    const currentSql = getCurrentSql();
    const content = { ...activeArtifact.content as SqlContent, sqlTemplate: currentSql };
    const note = prompt('Change note:');
    if (note) saveVersion(activeArtifact.artifactId, content, note);
  };

  const handlePromote = () => {
    if (!activeArtifact) return;
    if (confirm(`Promote version ${activeArtifact.version} to live?`)) {
      promote(activeArtifact.artifactId, activeArtifact.id);
    }
  };

  const sqlContent = activeArtifact?.content.type === 'sql' ? activeArtifact.content as SqlContent : null;

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex items-center gap-2 px-2 py-1 border-b border-tower-border/50 shrink-0">
        <select
          value={selectedId}
          onChange={(e) => setSelectedId(e.target.value)}
          className="bg-tower-bg text-tower-text text-xs border border-tower-border rounded px-1 py-0.5 max-w-[180px]"
        >
          <option value="">Select artifact...</option>
          {artifacts.map((a) => (
            <option key={a.artifactId} value={a.artifactId}>
              {a.name} (v{a.version})
            </option>
          ))}
        </select>

        <button onClick={handleTest} disabled={!activeArtifact || testing}
          className="text-xs px-2 py-0.5 bg-success hover:bg-success disabled:bg-tower-border text-textOnPrimary rounded">
          {testing ? 'Running...' : 'Test'}
        </button>
        <button onClick={handleSave} disabled={!activeArtifact}
          className="text-xs px-2 py-0.5 bg-primary hover:bg-primary disabled:bg-tower-border text-textOnPrimary rounded">
          Save Draft
        </button>
        {activeArtifact && !activeArtifact.isLive && (
          <button onClick={handlePromote}
            className="text-xs px-2 py-0.5 bg-warning hover:bg-warning text-textOnPrimary rounded">
            Promote
          </button>
        )}
        {activeArtifact && activeArtifact.isLive && (
          <button
            onClick={() => {
              const barnyardUrl = `https://${window.location.hostname.replace('app.', '')}`;
              const query = encodeURIComponent(`Use the ${activeArtifact.name} tool to show me sample results`);
              window.open(`${barnyardUrl}/?q=${query}`, '_blank');
            }}
            className="text-xs px-2 py-0.5 bg-purpleToken hover:bg-purpleToken text-textOnPrimary rounded"
            title="Open Barnyard and test this tool with an agent"
          >
            Verify with Agent
          </button>
        )}

        {activeArtifact && (
          <div className="ml-auto flex items-center gap-2 text-xs text-tower-muted">
            {(() => {
              const ds = (activeArtifact.dataSource || '').toLowerCase();
              const product = ds.includes('redshift') || ds.includes('maxsense') ? 'maxsense' : 'cvie';
              const sel = useArtifactStore.getState().activeDataSources[product];
              return sel ? <span className="text-success border border-success/40 rounded px-1.5 py-0.5 text-[10px]" title="Selected via Env Switcher">{sel}</span> : null;
            })()}
            <span className={`w-2 h-2 rounded-full ${activeArtifact.isLive ? 'bg-success' : 'bg-warning'}`} />
            <span>v{activeArtifact.version}</span>
            {activeArtifact.validation.status !== 'pending' && (
              <span className={activeArtifact.validation.status === 'passed' ? 'text-success' : 'text-error'}>
                {activeArtifact.validation.testResultSummary}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Copilot draft proposal banner (KB Doc 139) */}
      {copilotDraft && (
        <div className="flex items-center gap-2 px-2 py-1.5 bg-success-bg/30 border-y border-success/30 shrink-0">
          <span className="text-[11px] text-success flex-1 truncate" title={copilotDraft.changeNote}>
            Copilot loaded v{copilotDraft.version}: {copilotDraft.changeNote.replace(/^\[Copilot[^\]]*\]\s*/, '')}
          </span>
          <button
            onClick={() => {
              // Revert the editor to the live version — the draft stays in Version History
              if (activeArtifact) requestEditorLoad((activeArtifact.content as SqlContent).sqlTemplate);
              setDismissedDraftId(copilotDraft.id);
            }}
            className="text-[11px] px-2 py-0.5 bg-tower-surface border border-tower-border hover:bg-tower-border text-tower-text rounded">
            Revert to live
          </button>
          <span className="text-[10px] text-tower-muted">diff in Version History · promote stays human-only</span>
          <button
            onClick={() => setDismissedDraftId(copilotDraft.id)}
            className="text-[11px] px-1.5 py-0.5 text-tower-muted hover:text-tower-text">
            Dismiss
          </button>
        </div>
      )}

      {/* Editor + Params side-by-side */}
      <div className="flex-1 flex overflow-hidden">
        <div ref={editorRef} className="flex-1 overflow-auto" />

        {sqlContent && sqlContent.params.length > 0 && (
          <div className="w-48 border-l border-tower-border/50 p-2 overflow-y-auto shrink-0">
            <div className="text-xs text-tower-muted font-semibold mb-2">Parameters</div>
            {sqlContent.params.map((p) => (
              <div key={p.name} className="mb-2">
                <label className="text-xs text-tower-muted block mb-0.5">
                  {p.name} {p.required && <span className="text-error">*</span>}
                </label>
                <input
                  type={p.type === 'number' ? 'number' : 'text'}
                  value={paramValues[p.name] || ''}
                  onChange={(e) => setParamValues(prev => ({ ...prev, [p.name]: e.target.value }))}
                  placeholder={p.description}
                  className="w-full bg-tower-bg border border-tower-border rounded px-1 py-0.5 text-xs text-tower-text"
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
